import type { HTMLAttributes } from "react";

import { cn } from "../lib/cn";

export interface FunnelStripStep {
  /** Alleen voor de toegankelijke omschrijving — de strip toont zelf geen tekst. */
  label: string;
  count: number;
}

export interface FunnelStripProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  ariaLabel: string;
  steps: FunnelStripStep[];
  /** Bouwt de voorleestekst per stap; zonder deze prop leest de strip alleen zijn `ariaLabel`. */
  describeStep?: (step: FunnelStripStep) => string;
}

/**
 * De compacte funnel van de kaart-mockup: één segment per stap, even breed, waarbij de dekking de
 * verhouding draagt in plaats van een cijfer. Bewust zónder stapnamen — op een kaart in een raster
 * is daar geen plaats voor, en de ruwe broncodes van de bron (`INTAKE/READYFORPROCESSING`) horen
 * sowieso niet in beeld. Wie de stappen mét naam en telling wil, gebruikt `FunnelBar`.
 */
const OPACITY_CLASSES = [
  "opacity-100",
  "opacity-80",
  "opacity-[.6]",
  "opacity-[.45]",
  "opacity-[.3]",
  "opacity-20",
] as const;

export function FunnelStrip({
  ariaLabel,
  steps,
  describeStep,
  className,
  ...props
}: FunnelStripProps) {
  const max = steps.reduce((highest, step) => Math.max(highest, step.count), 0);

  return (
    <div
      aria-label={ariaLabel}
      className={cn("flex gap-1", className)}
      role="img"
      {...props}
    >
      {steps.map((step, index) => {
        // Een stap met een telling blijft altijd zichtbaar: nooit zo doorzichtig dat hij van een
        // lege stap niet te onderscheiden is.
        const shareIndex =
          max <= 0 || step.count <= 0
            ? OPACITY_CLASSES.length - 1
            : Math.min(
                OPACITY_CLASSES.length - 2,
                Math.round(
                  (1 - step.count / max) * (OPACITY_CLASSES.length - 2),
                ),
              );
        return (
          <span
            className={cn(
              "h-1.5 flex-1 rounded-full",
              step.count > 0 ? "bg-[var(--secondary)]" : "bg-[var(--border)]",
              OPACITY_CLASSES[shareIndex],
            )}
            key={`${step.label}-${index}`}
            title={describeStep?.(step)}
          />
        );
      })}
    </div>
  );
}
